import { useEffect, useState, useSyncExternalStore } from "react";
import type { TestSignalPath } from "@visioni/a3s-monitoring/app";
import { TEST_SIGNAL_COOLDOWN_MS, isOnCooldown } from "./cooldown";

type FiredAt = Readonly<Partial<Record<TestSignalPath, number>>>;

export interface TestSignalCooldown {
  isCoolingDown: (signalPath: TestSignalPath) => boolean;
  markFired: (signalPath: TestSignalPath) => void;
}

/** Reads the hold for one action out of a snapshot. Shared by the hook and its tests. */
export function isCoolingDown(firedAt: FiredAt, signalPath: TestSignalPath, now: number): boolean {
  return isOnCooldown(firedAt[signalPath], now);
}

function without(firedAt: FiredAt, signalPath: TestSignalPath): FiredAt {
  const remaining: Partial<Record<TestSignalPath, number>> = { ...firedAt };
  delete remaining[signalPath];
  return remaining;
}

/**
 * Records when each action last fired and wakes its subscribers once a hold
 * has elapsed.
 *
 * The timer exists only to wake them: whether an action is still held is
 * always decided against the wall clock, so a timer that runs late — a
 * backgrounded tab, a suspended device — cannot release an action early.
 */
export function createTestSignalCooldownStore() {
  let firedAt: FiredAt = {};
  const releaseTimers = new Map<TestSignalPath, ReturnType<typeof setTimeout>>();
  const subscribers = new Set<() => void>();

  const publish = (next: FiredAt) => {
    firedAt = next;
    for (const notify of subscribers) notify();
  };

  const release = (signalPath: TestSignalPath) => {
    releaseTimers.delete(signalPath);
    publish(without(firedAt, signalPath));
  };

  return {
    subscribe: (notify: () => void) => {
      subscribers.add(notify);
      return () => {
        subscribers.delete(notify);
      };
    },
    /** Stable between changes, as `useSyncExternalStore` requires of a snapshot. */
    getFiredAt: () => firedAt,
    markFired: (signalPath: TestSignalPath) => {
      const pending = releaseTimers.get(signalPath);
      if (pending !== undefined) clearTimeout(pending);
      releaseTimers.set(
        signalPath,
        setTimeout(() => release(signalPath), TEST_SIGNAL_COOLDOWN_MS),
      );
      publish({ ...firedAt, [signalPath]: Date.now() });
    },
    /** Drops pending releases so a store left behind cannot wake a gone component. */
    dispose: () => {
      for (const timer of releaseTimers.values()) clearTimeout(timer);
      releaseTimers.clear();
      subscribers.clear();
    },
  };
}

/**
 * Holds each test signal action for {@link TEST_SIGNAL_COOLDOWN_MS} after it
 * fires, and releases it without waiting for an unrelated re-render.
 */
export function useTestSignalCooldown(): TestSignalCooldown {
  const [store] = useState(createTestSignalCooldownStore);
  const firedAt = useSyncExternalStore(store.subscribe, store.getFiredAt, store.getFiredAt);

  useEffect(() => () => store.dispose(), [store]);

  return {
    isCoolingDown: (signalPath) => isCoolingDown(firedAt, signalPath, Date.now()),
    markFired: store.markFired,
  };
}
